import { PayloadAction } from '@reduxjs/toolkit'
import Creation from 'Entity/Creation'
import {
    initialLayoutState,
    StudioState,
    createNewPageData,
    PageData,
    generateTempUUID,
} from '../initialState'
import { syncPageMobileFromDesktop } from './mobileLayoutUtils'

// Backend (PHP/Laravel) can return [] for empty objects and vice versa.
// Ensure correct types for Immer compatibility.
const ensureObject = <T>(val: T | any[]): T => (Array.isArray(val) ? {} as T : val || {} as T)
const ensureArray = <T>(val: T[] | Record<string, any>): T[] => (Array.isArray(val) ? val : [])

export const canvasReducer = {
    setCoverImage: (
        state: StudioState,
        action: PayloadAction<StudioState['coverImage']>,
    ) => {
        state.coverImage = action.payload || null
    },
    setCreationData: (state: StudioState, action: PayloadAction<Creation>) => {
        const creation = action.payload
        state.creationId = creation.id
        state.lastSavedPastLength = 0

        if (creation.data && creation.data.pages) {
            const restoredPages: Record<string, PageData> = {}
            const restoredPageIds: string[] = creation.data.pageIds || []

            restoredPageIds.forEach((pageId) => {
                const pageData = creation.data.pages[pageId]
                if (pageData) {
                    const clonedPageData = JSON.parse(JSON.stringify(pageData))
                    const rawLayouts = clonedPageData.layouts || {}
                    const layouts = {
                        desktop: {
                            ...initialLayoutState,
                            items: ensureObject(rawLayouts.desktop?.items),
                            itemIds: ensureArray(rawLayouts.desktop?.itemIds),
                        },
                        mobile: {
                            ...initialLayoutState,
                            items: ensureObject(rawLayouts.mobile?.items),
                            itemIds: ensureArray(rawLayouts.mobile?.itemIds),
                        },
                    }

                    restoredPages[pageId] = {
                        ...createNewPageData(
                            pageId,
                            clonedPageData.name || 'Untitled',
                        ),
                        ...clonedPageData,
                        id: pageId,
                        assets: ensureObject(clonedPageData.assets),
                        layouts,
                        selectedItemIds: ensureArray(clonedPageData.selectedItemIds),
                    }

                    // Ensure mobile always exists and is generated from desktop when missing.
                    if (
                        !layouts.mobile ||
                        layouts.mobile.itemIds.length === 0 ||
                        Object.keys(layouts.mobile.items || {}).length === 0 ||
                        layouts.mobile.itemIds.length !==
                            layouts.desktop.itemIds.length
                    ) {
                        syncPageMobileFromDesktop(restoredPages[pageId])
                    }
                }
            })

            state.pages = restoredPages
            state.pageIds = restoredPageIds
            state.coverImage = creation.coverImage || null
            state.activePageId =
            creation.data.activePageId || restoredPageIds[0] || null
        } else {
            const pageId = generateTempUUID()
            const newPage = createNewPageData(pageId, 'Page 1')
            state.pages = { [newPage.id]: newPage }
            state.pageIds = [newPage.id]
            state.activePageId = newPage.id
            state.coverImage = creation.coverImage || null
        }
    },
}
